import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { Button } from "./Button";

export interface DateRange {
  end: Date | null;
  start: Date | null;
}

export interface DateRangePickerProps {
  ariaLabel?: string;
  className?: string;
  defaultOpen?: boolean;
  defaultValue?: DateRange;
  label?: string;
  locale?: string;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  value?: DateRange;
  weekStartsOn?: 0 | 1;
}

const EMPTY_RANGE: DateRange = { end: null, start: null };

// Native `Date` only — dependency-thin and air-gap-clean. All arithmetic runs
// in the local timezone via the y/m/d constructor so a date is never shifted
// across a day boundary by UTC parsing.
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function addMonths(date: Date, amount: number): Date {
  const firstOfTarget = new Date(
    date.getFullYear(),
    date.getMonth() + amount,
    1,
  );
  const clampedDay = Math.min(
    date.getDate(),
    daysInMonth(firstOfTarget.getFullYear(), firstOfTarget.getMonth()),
  );
  return new Date(
    firstOfTarget.getFullYear(),
    firstOfTarget.getMonth(),
    clampedDay,
  );
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function weekdayOffset(date: Date, weekStartsOn: 0 | 1): number {
  return (date.getDay() - weekStartsOn + 7) % 7;
}

function dateKey(date: Date): string {
  const year = date.getFullYear().toString().padStart(4, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildWeeks(viewDate: Date, weekStartsOn: 0 | 1): Date[][] {
  const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const leading = weekdayOffset(monthStart, weekStartsOn);
  const gridStart = addDays(monthStart, -leading);
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_unused, day) =>
      addDays(gridStart, week * 7 + day),
    ),
  );
}

export function DateRangePicker({
  ariaLabel,
  className,
  defaultOpen = false,
  defaultValue,
  label,
  locale,
  onChange,
  placeholder = "Select date range",
  value,
  weekStartsOn = 0,
}: DateRangePickerProps) {
  const [internalRange, setInternalRange] = useState<DateRange>(
    defaultValue ?? EMPTY_RANGE,
  );
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [focusedTime, setFocusedTime] = useState<number>(() =>
    startOfDay(defaultValue?.start ?? new Date()).getTime(),
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const focusedDayRef = useRef<HTMLButtonElement>(null);
  const shouldFocusDayRef = useRef(defaultOpen);
  const wasOpenRef = useRef(defaultOpen);

  const labelId = useId();
  const monthLabelId = useId();

  const range = value ?? internalRange;
  const focusedDate = useMemo(() => new Date(focusedTime), [focusedTime]);

  const formatters = useMemo(
    () => ({
      day: new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      full: new Intl.DateTimeFormat(locale, { dateStyle: "full" }),
      month: new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
      }),
      weekdayLong: new Intl.DateTimeFormat(locale, { weekday: "long" }),
      weekdayShort: new Intl.DateTimeFormat(locale, { weekday: "short" }),
    }),
    [locale],
  );

  const weeks = useMemo(
    () => buildWeeks(focusedDate, weekStartsOn),
    [focusedDate, weekStartsOn],
  );

  const weekdays = useMemo(() => {
    // 2023-01-01 is a Sunday; offset by weekStartsOn to order the header row.
    const reference = new Date(2023, 0, 1);
    return Array.from({ length: 7 }, (_, index) => {
      const day = addDays(reference, weekStartsOn + index);
      return {
        long: formatters.weekdayLong.format(day),
        short: formatters.weekdayShort.format(day),
      };
    });
  }, [formatters, weekStartsOn]);

  const monthLabel = formatters.month.format(
    new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1),
  );

  const startTime = range.start ? startOfDay(range.start).getTime() : null;
  const endTime = range.end ? startOfDay(range.end).getTime() : null;

  // Re-anchor the focused day each time the popover opens.
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) {
      return;
    }
    wasOpenRef.current = true;
    const base = range.start ?? range.end ?? new Date();
    setFocusedTime(startOfDay(base).getTime());
    shouldFocusDayRef.current = true;
  }, [isOpen, range.end, range.start]);

  useLayoutEffect(() => {
    if (isOpen && shouldFocusDayRef.current) {
      focusedDayRef.current?.focus();
      shouldFocusDayRef.current = false;
    }
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handlePointerDown(event: MouseEvent) {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node | null)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isOpen]);

  function commitRange(next: DateRange) {
    if (value === undefined) {
      setInternalRange(next);
    }
    onChange?.(next);
  }

  function selectDate(date: Date) {
    const day = startOfDay(date);
    let next: DateRange;
    if (range.start === null || range.end !== null) {
      next = { end: null, start: day };
    } else if (day.getTime() < startOfDay(range.start).getTime()) {
      // Enforce start <= end: a second pick before the anchor swaps the ends.
      next = { end: range.start, start: day };
    } else {
      next = { end: day, start: range.start };
    }
    commitRange(next);
    setFocusedTime(day.getTime());
    shouldFocusDayRef.current = true;
  }

  function clearRange() {
    commitRange(EMPTY_RANGE);
  }

  function closePopover(returnFocus: boolean) {
    setIsOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }

  function goToMonth(amount: number) {
    setFocusedTime(addMonths(focusedDate, amount).getTime());
  }

  function handleGridKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    let nextDate: Date;
    switch (event.key) {
      case "ArrowLeft":
        nextDate = addDays(focusedDate, -1);
        break;
      case "ArrowRight":
        nextDate = addDays(focusedDate, 1);
        break;
      case "ArrowUp":
        nextDate = addDays(focusedDate, -7);
        break;
      case "ArrowDown":
        nextDate = addDays(focusedDate, 7);
        break;
      case "Home":
        nextDate = addDays(
          focusedDate,
          -weekdayOffset(focusedDate, weekStartsOn),
        );
        break;
      case "End":
        nextDate = addDays(
          focusedDate,
          6 - weekdayOffset(focusedDate, weekStartsOn),
        );
        break;
      case "PageUp":
        nextDate = addMonths(focusedDate, event.shiftKey ? -12 : -1);
        break;
      case "PageDown":
        nextDate = addMonths(focusedDate, event.shiftKey ? 12 : 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        selectDate(focusedDate);
        return;
      case "Escape":
        event.preventDefault();
        closePopover(true);
        return;
      default:
        return;
    }
    event.preventDefault();
    setFocusedTime(startOfDay(nextDate).getTime());
    shouldFocusDayRef.current = true;
  }

  function formatDay(date: Date): string {
    return formatters.day.format(date);
  }

  const triggerLabel =
    range.start && range.end
      ? `${formatDay(range.start)} – ${formatDay(range.end)}`
      : range.start
        ? `${formatDay(range.start)} – …`
        : placeholder;

  const readout =
    range.start && range.end
      ? `${formatDay(range.start)} – ${formatDay(range.end)}`
      : range.start
        ? `${formatDay(range.start)} – select end date`
        : "No dates selected";

  const rootClasses = ["marker-date-range-picker"];
  if (className) {
    rootClasses.push(className);
  }

  const triggerHasSelection = range.start !== null;

  return (
    <div className={rootClasses.join(" ")} ref={rootRef}>
      {label ? (
        <span className="marker-field__label" id={labelId}>
          {label}
        </span>
      ) : null}
      <button
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={label ? undefined : (ariaLabel ?? "Date range")}
        aria-labelledby={label ? labelId : undefined}
        className={[
          "marker-date-range-picker__trigger",
          triggerHasSelection
            ? ""
            : "marker-date-range-picker__trigger--placeholder",
        ].join(" ")}
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        type="button"
      >
        {triggerLabel}
      </button>
      {isOpen ? (
        <div
          aria-label={label ?? ariaLabel ?? "Choose date range"}
          className="marker-date-range-picker__popover"
          role="dialog"
        >
          <div className="marker-date-range-picker__nav">
            <button
              aria-label="Previous month"
              className="marker-date-range-picker__nav-button"
              onClick={() => {
                goToMonth(-1);
              }}
              type="button"
            >
              {"‹"}
            </button>
            <span className="marker-date-range-picker__month" id={monthLabelId}>
              {monthLabel}
            </span>
            <button
              aria-label="Next month"
              className="marker-date-range-picker__nav-button"
              onClick={() => {
                goToMonth(1);
              }}
              type="button"
            >
              {"›"}
            </button>
          </div>
          <div
            aria-labelledby={monthLabelId}
            className="marker-date-range-picker__grid"
            onKeyDown={handleGridKeyDown}
            role="grid"
          >
            <div className="marker-date-range-picker__weekdays" role="row">
              {weekdays.map((weekday) => (
                <span
                  aria-label={weekday.long}
                  className="marker-date-range-picker__weekday"
                  key={weekday.long}
                  role="columnheader"
                >
                  {weekday.short}
                </span>
              ))}
            </div>
            {weeks.map((week, weekIndex) => (
              <div
                className="marker-date-range-picker__week"
                key={`week-${weekIndex.toString()}`}
                role="row"
              >
                {week.map((date) => {
                  const cellTime = startOfDay(date).getTime();
                  const isStart = startTime !== null && cellTime === startTime;
                  const isEnd = endTime !== null && cellTime === endTime;
                  const isMiddle =
                    startTime !== null &&
                    endTime !== null &&
                    cellTime > startTime &&
                    cellTime < endTime;
                  const isSelected = isStart || isEnd || isMiddle;
                  const isOutside = !isSameMonth(date, focusedDate);
                  const isToday = isSameDay(date, new Date());
                  const isFocusTarget = isSameDay(date, focusedDate);

                  const dayClasses = ["marker-date-range-picker__day"];
                  if (isOutside) {
                    dayClasses.push("marker-date-range-picker__day--outside");
                  }
                  if (isToday) {
                    dayClasses.push("marker-date-range-picker__day--today");
                  }
                  if (isMiddle) {
                    dayClasses.push("marker-date-range-picker__day--in-range");
                  }
                  if (isStart) {
                    dayClasses.push(
                      "marker-date-range-picker__day--range-start",
                    );
                  }
                  if (isEnd) {
                    dayClasses.push("marker-date-range-picker__day--range-end");
                  }

                  return (
                    <div
                      aria-selected={isSelected}
                      className="marker-date-range-picker__cell"
                      key={dateKey(date)}
                      role="gridcell"
                    >
                      <button
                        ref={isFocusTarget ? focusedDayRef : undefined}
                        aria-current={isToday ? "date" : undefined}
                        aria-label={formatters.full.format(date)}
                        className={dayClasses.join(" ")}
                        data-date={dateKey(date)}
                        onClick={() => {
                          selectDate(date);
                        }}
                        tabIndex={isFocusTarget ? 0 : -1}
                        type="button"
                      >
                        {date.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="marker-date-range-picker__footer">
            <span className="marker-date-range-picker__readout">{readout}</span>
            <Button
              onClick={() => {
                clearRange();
              }}
              size="sm"
              variant="ghost"
            >
              Clear
            </Button>
          </div>
          <span
            aria-live="polite"
            className="marker-date-range-picker__sr-only"
            role="status"
          >
            {monthLabel}
          </span>
        </div>
      ) : null}
    </div>
  );
}
